import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppContext } from '../../../../shared/context/AppContext';
import { useAnalytics } from '../../../../shared/hooks/useAnalytics';
import { EVENTS } from '../../../../shared/services/analyticsEvents';
import { TUTORIAL_STEPS } from '../../domain/services/tutorialStepDefinitions';
import { markTutorialCompleted } from '../../data/services/tutorialPersistenceService';

const TutorialContext = createContext();

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within TutorialProvider');
  }
  return context;
};

export const TutorialProvider = ({ children }) => {
  const { user, setUser } = useAppContext();
  const { trackEvent } = useAnalytics();
  const location = useLocation();
  const navigate = useNavigate();

  const [isActive, setIsActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const hasStartedRef = useRef(false);

  // Determine whether to activate tutorial on user load
  useEffect(() => {
    if (!user || hasStartedRef.current) return;

    const tutorialCompleted =
      user.tutorialCompleted === true ||
      user.tutorialCompleted === 1 ||
      user.tutorialCompleted === '1';

    const onboardingCompleted =
      user.onboardingCompleted === true ||
      user.onboardingCompleted === 1 ||
      user.onboardingCompleted === '1';

    const roleCompleted =
      user.roleCompleted === true ||
      user.roleCompleted === 1 ||
      user.roleCompleted === '1';

    if (!tutorialCompleted && onboardingCompleted && roleCompleted) {
      hasStartedRef.current = true;
      setIsActive(true);
      setCurrentStepIndex(0);
      trackEvent(EVENTS.TUTORIAL_STARTED, {});
    }
  }, [user, trackEvent]);

  const persistCompletion = useCallback(() => {
    if (!user) return;
    const userId = user.id || user.userId;
    const updatedUser = { ...user, tutorialCompleted: true };
    setUser(updatedUser);
    localStorage.setItem('user_data', JSON.stringify(updatedUser));
    markTutorialCompleted(userId);
  }, [user, setUser]);

  const completeTutorial = useCallback(() => {
    setIsActive(false);
    trackEvent(EVENTS.TUTORIAL_COMPLETED, { totalSteps: TUTORIAL_STEPS.length });
    persistCompletion();
  }, [trackEvent, persistCompletion]);

  const skipTutorial = useCallback(() => {
    const step = TUTORIAL_STEPS[currentStepIndex];
    setIsActive(false);
    trackEvent(EVENTS.TUTORIAL_SKIPPED, {
      stepId: step?.id,
      stepIndex: currentStepIndex,
      totalSteps: TUTORIAL_STEPS.length,
    });
    persistCompletion();
  }, [currentStepIndex, trackEvent, persistCompletion]);

  const advanceStep = useCallback(() => {
    const step = TUTORIAL_STEPS[currentStepIndex];

    trackEvent(EVENTS.TUTORIAL_STEP_COMPLETED, {
      stepId: step?.id,
      stepIndex: currentStepIndex,
      totalSteps: TUTORIAL_STEPS.length,
    });

    const nextIndex = currentStepIndex + 1;

    if (nextIndex >= TUTORIAL_STEPS.length) {
      completeTutorial();
      return;
    }

    setCurrentStepIndex(nextIndex);

    // Programmatically navigate if this step requires it
    if (step?.navigateAfter) {
      navigate(step.navigateAfter);
    }
  }, [currentStepIndex, completeTutorial, trackEvent, navigate]);

  // Event delegation for 'natural-click' steps
  useEffect(() => {
    if (!isActive) return;

    const step = TUTORIAL_STEPS[currentStepIndex];
    if (!step || step.advanceOn !== 'natural-click') return;

    const handler = (e) => {
      const target = document.querySelector(step.targetSelector);
      if (target && (target === e.target || target.contains(e.target))) {
        advanceStep();
      }
    };

    // Capture phase so we detect before any stopPropagation in child handlers
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [isActive, currentStepIndex, advanceStep]);

  const getCurrentStep = useCallback(() => {
    if (!isActive) return null;
    return TUTORIAL_STEPS[currentStepIndex] || null;
  }, [isActive, currentStepIndex]);

  // Check if the current step should be visible on this route
  const isStepVisibleOnRoute = useCallback(() => {
    const step = getCurrentStep();
    if (!step) return false;
    return location.pathname.startsWith(step.route);
  }, [getCurrentStep, location.pathname]);

  const value = {
    isActive,
    currentStepIndex,
    getCurrentStep,
    isStepVisibleOnRoute,
    advanceStep,
    skipTutorial,
    totalSteps: TUTORIAL_STEPS.length,
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};
